import { useState, useEffect, useCallback, useRef } from 'react';
import { leerUltimaActividad, marcarActividad } from '../utils/inactividad';

const useIdleTimer = ({
  timeout = 3600000,
  onIdle,
  warningTime = 60000,
  onWarning
}) => {
  const [isIdle, setIsIdle] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const timeoutIdRef = useRef(null);
  const warningTimeoutIdRef = useRef(null);
  const isWarningActiveRef = useRef(false);

  // La actividad se mide entre todas las pestañas (utils/inactividad): antes de
  // avisar o cerrar se mira la ultima marca compartida, y si otra pestaña se usó
  // hace poco se reprograma por lo que falta. Sin esto, una pestaña olvidada
  // cerraba la sesion de la que se estaba usando.
  const restante = useCallback(() => {
    const ultima = leerUltimaActividad();
    return ultima ? timeout - (Date.now() - ultima) : 0;
  }, [timeout]);

  const programar = useCallback(function programar(ms) {
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    if (warningTimeoutIdRef.current) clearTimeout(warningTimeoutIdRef.current);

    if (onWarning && warningTime > 0) {
      warningTimeoutIdRef.current = setTimeout(() => {
        const falta = restante();
        if (falta > warningTime) { programar(falta); return; }
        setShowWarning(true);
        isWarningActiveRef.current = true;
        onWarning();
      }, Math.max(ms - warningTime, 0));
    }

    timeoutIdRef.current = setTimeout(() => {
      // Con aviso activo decide el modal; sin aviso se vuelve a mirar la marca.
      if (!isWarningActiveRef.current) {
        const falta = restante();
        if (falta > 0) { programar(falta); return; }
      }
      setIsIdle(true);
      onIdle();
    }, ms);
  }, [onIdle, warningTime, onWarning, restante]);

  const resetTimer = useCallback(() => {
    // Si el modal de advertencia está activo, NO resetear
    if (isWarningActiveRef.current) {
      return;
    }

    setIsIdle(false);
    setShowWarning(false);
    marcarActividad();
    programar(timeout);
  }, [timeout, programar]);

  const resetTimerManual = useCallback(() => {
    isWarningActiveRef.current = false;
    marcarActividad(true);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (warningTimeoutIdRef.current) {
        clearTimeout(warningTimeoutIdRef.current);
      }
    };
  }, [resetTimer]);

  return { isIdle, showWarning, resetTimer: resetTimerManual };
};

export default useIdleTimer;
